/*
===============================================================================================================
                                 Copyright 2024-2024, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { LocaleContext } from '@/contexts/locale-context';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
/**
  * Locations component
  * This component is responsible for rendering the nearest locations section
  * It is a placeholder for the actual locations data
  * @param {Object} locationsData - The locations data
  * @param {string} selectorId - The selector id
  * @returns {JSX.Element}
*/
function Locations({ locationsData, selectorId }) {
  const { translate } = useContext(LocaleContext);
  return (
    <div className="container" id={selectorId}>
      <div className="nearest-locations">
        {translate('customisation.nearestLocations')}
      </div>
      <div className="locations-list">
        {locationsData.map((location) => (
          <div key={location.name} className="location">
            <div className="location-name">
              {location.name}
            </div>
            <div className="location-address">
              {location.street && (
              <div>
                {location.street}
              </div>
              )}
              {location.city && (
              <div>
                {location.city}
              </div>
              )}
              {location.state && (
              <div>
                {location.state}
              </div>
              )}
            </div>
            <div className="distance">
              {location.distance}
            </div>
          </div>
        ))}
      </div>
      <style jsx>
        {`

          .container {
            display: flex;
            flex-direction: column;
            width: 100%;
          }

          .nearest-locations {
            border-bottom: 1px solid var(--color-light-grey);
            border-top: 1px solid var(--color-light-grey);
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-H1);
            margin-top: 10px;
            text-align: left;
            padding: 8px 0;
            width: 100%;
          }

          .locations-list {
            display: flex;
            line-height: 20px;
            padding-top: 16px;
            padding-left: 5px;
            width: 100%;
          }

          .locations-list > div:first-child {
            border-left: initial;
            border-right: 1px solid var(--color-light-grey);
            padding-left: 0;
            padding-right: 0;
          }

          .locations-list > div:nth-child(2) {
            padding-left: 20px;
            padding-right: 20px;
          }

          .location {
            flex: 1;
          }

          .location-name {
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-H1);
            line-height: 18px;
          }

          .location-address {
            display: flex;
            flex-direction: column;
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-body);
          }

          .location-address > div {
            display: flex;
            flex-direction: column;
          }

          .distance {
            align-items: flex-end;
            flex: 1 1 auto;
            font-size: var(--font-size-H4);
            font-weight: var(--font-weight-body);
            line-height: 18px;
          }

          @media only screen and (max-width: 1024px) {
            .locations-list {
              flex-direction: column;
              padding: 0;
            }

            .locations-list > div {
              border-bottom: 1px solid var(--color-light-deeper-grey);
            }

            .locations-list > div:first-child {
              border-left: unset;
              border-right: unset;
              flex-grow: 1;
              min-width: 80%;
              padding: 16px 0 16px 0;
            }

            .locations-list > div:last-child {
              border-bottom: 0;
              flex-grow: 0;
              padding: 16px 0 0 0;
            }

            .location-address {
              flex-direction: row;
            }

            .distance {
              align-items: flex-end;
              justify-content: flex-end;
            }
          }

          @media only screen and (max-width: 420px) {
            .locations-list > div:first-child {
              flex-grow: 1;
              min-width: 80%;
            }

            .locations-list > div:last-child {
              flex-grow: 0;
            }
          }
        `}
      </style>
    </div>
  );
}

Locations.defaultProps = {
  locationsData: [],
};

Locations.propTypes = {
  selectorId: PropTypes.string.isRequired,
  locationsData: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    distance: PropTypes.string,
  })),
};

export default Locations;
